import { buildLinearTrendline } from "./dashboardTrendline";
import {
    bucketsToMonthlySeries,
    buildGameAggregatesFromStats,
    computeDayPopularityFromDayBuckets,
    sumBucketValues,
} from "./dashboardAggregations";
import { formatMonthAxisLabel } from "./dashboardReportPeriods";

/**
 * Chart/summary view model for the fixed 12-month report (not tied to URL date filters).
 */
export function buildFixedReportViewModel({
    colors,
    periodLabel,
    monthKeys,
    tableStats,
    statsPayload,
    experienceNameById,
    weekdayLabels,
}) {
    const keys = monthKeys?.length === 12
        ? monthKeys
        : (statsPayload.monthKeys || []);
    if (keys.length !== 12) {
        throw new Error("12-month report requires exactly 12 monthKeys from the fetch period.");
    }
    const monthLabels = keys.map((key) => formatMonthAxisLabel(key));
    const resolvedPeriodLabel = periodLabel
        || `${formatMonthAxisLabel(keys[0], "MMM YYYY")} – ${formatMonthAxisLabel(keys[11], "MMM YYYY")}`;

    const bookingsByMonth = bucketsToMonthlySeries(statsPayload.bookingsByMonth, keys);
    const playersByMonth = bucketsToMonthlySeries(statsPayload.playersByMonth, keys);
    const bookingsTrend = buildLinearTrendline(bookingsByMonth);

    const totalBookings = Number(tableStats?.count) || sumBucketValues(statsPayload.bookingsByMonth);
    const totalPlayers = Number(tableStats?.total_number_of_players) || sumBucketValues(statsPayload.playersByMonth);
    const avgPlayersPerBooking = totalBookings > 0 ? totalPlayers / totalBookings : 0;
    const avgLocationRating = Number(tableStats?.average_location_rating) || 0;
    const hasLocationRating = avgLocationRating > 0;

    const monthlyDatasets = [
        {
            label: "Bookings",
            data: bookingsByMonth,
            backgroundColor: colors.seriesPrimaryFill,
            borderColor: colors.seriesPrimaryStroke,
            borderWidth: 1,
            borderRadius: 4,
        },
        {
            label: "Players",
            data: playersByMonth,
            backgroundColor: colors.seriesSecondaryFill,
            borderWidth: 1,
            borderRadius: 4,
        },
        {
            type: "line",
            label: "Trend (bookings)",
            data: bookingsTrend,
            borderColor: colors.trendLine,
            borderWidth: 2,
            pointRadius: 0,
            borderDash: [6, 4],
            fill: false,
        },
    ];

    const { rankedByCount } = buildGameAggregatesFromStats(
        statsPayload.playByExperience,
        experienceNameById,
    );
    const experienceLabels = rankedByCount.slice(0, 8).map((item) => item.name);
    const experienceDatasets = [
        {
            label: "Bookings",
            data: rankedByCount.slice(0, 8).map((item) => item.count),
            backgroundColor: colors.seriesTertiaryFill,
        },
    ];

    const dayPop = computeDayPopularityFromDayBuckets(
        statsPayload.bookingsByDay,
        statsPayload.playersByDay,
        weekdayLabels,
    );
    const weekdayDatasets = [
        {
            label: "Bookings",
            data: weekdayLabels.map((label) => dayPop.bookingCounts[label] || 0),
            backgroundColor: colors.seriesSecondaryFill,
        },
    ];

    const languageLabels = statsPayload.playByLanguage.map((bucket) => (bucket.key || "unknown").toUpperCase());
    const languageDatasets = [
        {
            label: "Bookings",
            data: statsPayload.playByLanguage.map((bucket) => bucket.value),
            backgroundColor: colors.seriesPrimaryFill,
        },
    ];

    return {
        periodLabel: resolvedPeriodLabel,
        summary: {
            totalBookings,
            totalPlayers,
            avgPlayersPerBooking,
            avgLocationRating,
            hasLocationRating,
        },
        monthLabels,
        monthlyDatasets,
        experienceLabels,
        experienceDatasets,
        weekdayLabels,
        weekdayDatasets,
        languageLabels,
        languageDatasets,
    };
}
